import { AuthService } from '../../services/auth.service';
import { INotificationMessage } from '../../sockets/notification.messages';
import { eventDataNoticeKey } from '../../helpers/event-data-delivery';
import { Component, computed, DestroyRef, effect, inject, signal, Type, untracked, ViewEncapsulation } from '@angular/core';
import { StorageUrlService } from 'src/app/services/storage-url.service';
import { IDownloadData } from '../../sockets/download.message';
import { IMentionData } from '../../sockets/mention.message';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { DomHub } from '../../sockets/dom.hub';
import { DownloadService } from '../../services/download.service';
import { OnboardingService } from '../../services/onboarding.service';
import { CoCoachService } from '../../services/co-coach.service';
import { environment } from '../../../environments/environment';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
} from '@angular/router';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-user-layout',
  template: `
    @if (!hideHeader() || smalScherm()) {
      <app-header></app-header>
    }
    <div class="content-wrapper" [class.hide-header]="omlijstingVol()"
         [class.met-co-coach]="coCoach.open() && !hideHeader()">
      <router-outlet></router-outlet>
    </div>
    <!-- Co-coach (variant C): op elke pagina met kopregel, want daar staat de
         knop. Zegt de server dat hij er niet is, dan staat hier niets en
         wordt het paneel (met speler en dialoog) ook niet geladen. -->
    @if (coCoachPaneel(); as paneel) {
      @if (!hideHeader() || smalScherm()) {
        <ng-container *ngComponentOutlet="paneel"></ng-container>
      }
    }
    <!-- Onboarding (docs/ONBOARDING.md): hier en niet op een pagina, zodat
         het venster op elke pagina kan openen en een rondleiding blijft
         staan als een stap naar een andere pagina gaat. -->
    <app-welkom-dialoog></app-welkom-dialoog>
    <app-rondleiding></app-rondleiding>
  `,
  styleUrls: ['./user-layout.component.scss'],
})
export class UserLayoutComponent {
  private storageUrlService = inject(StorageUrlService);
  private socket = inject(DomHub);
  private auth = inject(AuthService);
  private eventDataNotices = new Set<string>();
  private downloadService = inject(DownloadService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService);
  // Losse verwijzing nodig: takeUntilDestroyed() zonder argument werkt alleen
  // in een injectiecontext, en de aanroep hieronder zit in een subscribe.
  private destroyRef = inject(DestroyRef);
  public coCoach = inject(CoCoachService);
  /** Het paneel van de co-coach, pas geladen als de server zegt dat hij er is. */
  public coCoachPaneel = signal<Type<unknown> | null>(null);

  ROUTES_WITH_HIDE_HEADER = ['videos'];

  /**
   * `true`: geen kopregel (de videopagina). `'breed'`: alleen vanaf 992 px
   * geen kopregel (Search, Stats, spelersprofiel, playlist; eigenaar
   * 25-10-2026: "zoals bij match video's"); smaller is de pagina gewoon.
   */
  public hideHeader = signal<boolean | 'breed'>(false);

  /**
   * De omlijsting over het hele scherm. Bij `'breed'` alleen als de kopregel
   * ook echt weg is: smaller staat hij er met de tabbalk, en dan viel de
   * onderste 96 px van de pagina achter de tabbalk.
   */
  public omlijstingVol = computed(() => this.hideHeader() === true || (this.hideHeader() === 'breed' && !this.smalScherm()));

  /**
   * Op een smal scherm blijft de kopregel staan ook als de route hem
   * verbergt. De tabbalk zit namelijk IN dat element (header.component.html),
   * dus zonder kopregel is er op de videopagina geen navigatie meer
   * (eigenaar 19-09-2026: "in match video scherm zijn de icons weg"). Wat er
   * bovenin van de kopregel te zien was -- clublogo en avatar -- blijft daar
   * weg; dat doet _mobile.scss.
   */
  public smalScherm = signal(
    typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia('(max-width: 991.98px)').matches);

  constructor() {
    // Na het inloggen (of bij het openen van de app met een sessie) komt de
    // gebruiker altijd hier binnen: het moment om de onboarding op te halen.
    inject(OnboardingService).laad();
    // Of de co-coach er is: ook één keer per sessie. Zo ja, dan pas het
    // paneel ophalen (een eigen bundel), zodat de app zonder co-coach niet
    // groter wordt.
    this.coCoach.laad();
    effect(() => {
      if (this.coCoach.beschikbaar() && !untracked(this.coCoachPaneel)) {
        void import('../co-coach/co-coach.component').then((m) => this.coCoachPaneel.set(m.CoCoachComponent));
      }
    });
    this.volgSchermbreedte();
    this.listenForDownloadFile();
    this.listenForMentions();
    this.listenForEventData();
    this.listenForHideHeader();
  }

  /** Draaien of van venstergrootte wisselen: de tabbalk komt of gaat mee. */
  private volgSchermbreedte(): void {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    const vraag = window.matchMedia('(max-width: 991.98px)');
    const bijwerken = () => this.smalScherm.set(vraag.matches);
    bijwerken();
    if (vraag.addEventListener) {
      vraag.addEventListener('change', bijwerken);
      this.destroyRef.onDestroy(() => vraag.removeEventListener('change', bijwerken));
    }
  }

  private listenForDownloadFile(): void {
    this.socket.socket
      .fromEvent<IDownloadData>('download')
      .pipe(
        takeUntilDestroyed(),
        filter(
          (response) =>
            this.downloadService.downloads.indexOf(response.reference) > -1,
        ),
      )
      .subscribe((response) => {
        let filename = '';
        if (response.title.includes('.mp4')) {
          filename = response.title;
        } else {
          filename = response.title + '.mp4';
        }

        this.downloadService.downloadFile(
          this.storageUrlService.base + response.url,
          filename,
        );
        this.downloadService.removeDownload(response.reference);
      });
  }

  /**
   * Iemand heeft je met een @ genoemd in een bericht bij een event. Komt
   * binnen op de eigen socket; siq_herald stuurt hem alleen naar de genoemde
   * gebruiker. Klikken op de melding brengt je naar dat moment in de video.
   *
   * Hier hangt bewust geen filter onder: de server heeft al bepaald dat dit
   * bericht voor jou is, en een tweede controle in de browser zou alleen maar
   * kunnen afwijken.
   */
  private listenForMentions(): void {
    this.socket.socket
      .fromEvent<IMentionData>('mention')
      .pipe(takeUntilDestroyed())
      .subscribe((melding) => {
        const titel = this.translate.instant('MENTION.title', {
          naam: melding.byName || this.translate.instant('MENTION.someone'),
        });

        this.toastr
          .info(melding.comment, titel, { timeOut: 12000, closeButton: true, tapToDismiss: false })
          .onTap.pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.openMelding(melding));
      });
  }

  /**
   * Naar het event toe. Een event hangt aan een video of aan een wedstrijd;
   * `source` zegt welke van de twee. De starttijd gaat als queryparameter mee,
   * zodat de speler op het moment zelf begint en niet vooraan.
   */
  private openMelding(melding: IMentionData): void {
    if (!melding.reference) {
      return;
    }

    const pad = melding.source === 'match'
      ? ['/videos', 'matches', melding.reference]
      : ['/videos', melding.reference];

    void this.router.navigate(pad, { queryParams: { t: Math.floor(melding.start ?? 0) } });
  }

  private listenForEventData(): void {
    this.socket.socket.fromEvent<INotificationMessage>('dom')
      .pipe(takeUntilDestroyed())
      .subscribe(message => {
        const key = eventDataNoticeKey(message, this.auth.user?.club_id);
        if (!key || this.eventDataNotices.has(key)) return;
        this.eventDataNotices.add(key);
        this.toastr.success(
          this.translate.instant('EVENT_DATA.ready_message', { match: message.payload.title || message.sid }),
          this.translate.instant('EVENT_DATA.title'),
          { timeOut: 12000, closeButton: true, enableHtml: false },
        );
      });
  }

  private listenForHideHeader(): void {
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        const checkChildRoute = (route: ActivatedRouteSnapshot): boolean | 'breed' => {
          if (route.data['hideHeader']) {
            return route.data['hideHeader'] === 'breed' ? 'breed' : true;
          }
          for (const child of route.children) {
            const stand = checkChildRoute(child);
            if (stand) {
              return stand;
            }
          }
          return false;
        };

        this.hideHeader.set(checkChildRoute(this.activatedRoute.snapshot));
      });
  }
}
